<template>
  <TransitionRoot appear :show="true" as="template">
    <Dialog as="div" class="relative z-50" @close="$emit('close')">
      <div class="fixed inset-0 bg-black/40" />
      <div class="fixed inset-0 flex items-start justify-center overflow-y-auto p-4">
        <DialogPanel class="my-8 w-full max-w-4xl rounded-lg bg-white shadow-xl">
          <header class="flex items-start justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <DialogTitle class="text-lg font-semibold text-gray-900">
                {{ $gettext("Déclaration CTIF") }}
              </DialogTitle>
              <p class="mt-1 text-xs text-gray-500">
                {{ $gettext("Champs marqués d’un * = requis (art. 26 de la Loi). Les autres sont optionnels.") }}
              </p>
            </div>
            <button class="text-gray-400 hover:text-gray-700" @click="$emit('close')">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>

          <div v-if="loading" class="px-6 py-12 text-center text-sm text-gray-500">
            {{ $gettext("Préchargement des données…") }}
          </div>

          <form v-else-if="declaration" class="space-y-6 px-6 py-5" @submit.prevent="onSubmit">
            <div
              v-if="showValidationBanner && missingRequired.length > 0"
              ref="validationBanner"
              class="sticky top-0 z-10 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 shadow-sm"
            >
              <strong>{{ $gettext("Champs requis manquants :") }}</strong>
              <ul class="mt-1 list-inside list-disc">
                <li v-for="m in missingRequired" :key="m">{{ m }}</li>
              </ul>
            </div>

            <!-- Declarant -->
            <section>
              <h3 class="mb-3 text-sm font-semibold uppercase text-gray-700">{{ $gettext("Déclarant") }}</h3>
              <div class="grid grid-cols-2 gap-3">
                <CtifFieldRow v-model="declaration.declarant.last_name" :label="$gettext('Nom')" required />
                <CtifFieldRow v-model="declaration.declarant.first_name" :label="$gettext('Prénom')" required />
                <CtifFieldRow v-model="declaration.declarant.institution_name" :label="$gettext('Institution')" required />
                <CtifFieldRow v-model="declaration.declarant.institution_vat" :label="$gettext('N° d\'entreprise institution')" />
                <CtifFieldRow v-model="declaration.declarant.institution_street" :label="$gettext('Rue')" />
                <CtifFieldRow v-model="declaration.declarant.institution_number" :label="$gettext('Numéro')" />
                <CtifFieldRow v-model="declaration.declarant.institution_zipcode" :label="$gettext('Code postal')" />
                <CtifFieldRow v-model="declaration.declarant.institution_city" :label="$gettext('Localité')" />
                <CtifFieldRow v-model="declaration.declarant.phone" :label="$gettext('Téléphone')" />
                <CtifFieldRow v-model="declaration.declarant.email" :label="$gettext('E-mail')" type="email" />
                <CtifFieldRow v-model="declaration.declarant.case_reference" :label="$gettext('Référence dossier')" />
              </div>
            </section>

            <!-- Client -->
            <section>
              <h3 class="mb-3 text-sm font-semibold uppercase text-gray-700">{{ $gettext("Client") }}</h3>
              <CtifLegalEntityFieldset
                v-if="declaration.client.kind === 'legal_entity'"
                :model-value="declaration.client.legal_entity"
              />
              <CtifNaturalPersonFieldset
                v-else
                :model-value="declaration.client.natural_person"
                :title="$gettext('Personne physique')"
              />
            </section>

            <!-- Mandataries (Companies only) -->
            <section v-if="declaration.client.kind === 'legal_entity'">
              <div class="mb-3 flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase text-gray-700">{{ $gettext("Mandataires") }}</h3>
                <button type="button" class="text-xs text-indigo-600 hover:text-indigo-800" @click="addMandatary">
                  + {{ $gettext("Ajouter") }}
                </button>
              </div>
              <div class="space-y-3">
                <CtifNaturalPersonFieldset
                  v-for="(m, i) in declaration.mandataries"
                  :key="`mandatary-${i}`"
                  :model-value="m"
                  :title="`${$gettext('Mandataire')} #${i + 1}`"
                  removable
                  @remove="declaration.mandataries.splice(i, 1)"
                />
                <p v-if="declaration.mandataries.length === 0" class="text-xs italic text-gray-500">
                  {{ $gettext("Aucun mandataire.") }}
                </p>
              </div>
            </section>

            <!-- UBO (Companies only) -->
            <section v-if="declaration.client.kind === 'legal_entity'">
              <div class="mb-3 flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase text-gray-700">{{ $gettext("Bénéficiaires effectifs") }}</h3>
                <button type="button" class="text-xs text-indigo-600 hover:text-indigo-800" @click="addUbo">
                  + {{ $gettext("Ajouter") }}
                </button>
              </div>
              <div class="space-y-3">
                <CtifNaturalPersonFieldset
                  v-for="(u, i) in declaration.ultimate_beneficial_owners"
                  :key="`ubo-${i}`"
                  :model-value="u"
                  :title="`${$gettext('Bénéficiaire')} #${i + 1}`"
                  removable
                  address-optional
                  @remove="declaration.ultimate_beneficial_owners.splice(i, 1)"
                />
                <p v-if="declaration.ultimate_beneficial_owners.length === 0" class="text-xs italic text-gray-500">
                  {{ $gettext("Aucun bénéficiaire effectif.") }}
                </p>
              </div>
            </section>

            <!-- Operation -->
            <section>
              <h3 class="mb-3 text-sm font-semibold uppercase text-gray-700">
                {{ $gettext("Opération / faits suspects") }}
              </h3>
              <div class="grid grid-cols-2 gap-3">
                <CtifFieldRow v-model="declaration.operation.total_amount" :label="$gettext('Montant total')" />
                <CtifFieldRow v-model="declaration.operation.currency" :label="$gettext('Devise')" required />
                <CtifFieldRow v-model="declaration.operation.execution_date" :label="$gettext('Date d\'exécution')" type="date" />
                <label class="flex flex-col gap-1 text-sm">
                  <span class="font-medium text-gray-700">{{ $gettext("Transaction déjà effectuée ?") }}</span>
                  <select v-model="declaration.operation.already_executed" class="rounded-md border border-gray-300 px-3 py-2 text-sm">
                    <option :value="null">—</option>
                    <option :value="true">{{ $gettext("Oui") }}</option>
                    <option :value="false">{{ $gettext("Non") }}</option>
                  </select>
                </label>
              </div>
              <div class="mt-3">
                <CtifFieldRow
                  v-model="declaration.operation.description"
                  :label="$gettext('Description des événements / transactions / faits')"
                  multiline
                  required
                />
              </div>
              <div class="mt-3">
                <CtifFieldRow
                  v-model="declaration.suspicion_indicators"
                  :label="$gettext('Indices BC/FT')"
                  multiline
                  required
                />
              </div>
            </section>

            <!-- Criminal investigation -->
            <section>
              <h3 class="mb-3 text-sm font-semibold uppercase text-gray-700">{{ $gettext("Enquête pénale") }}</h3>
              <div class="grid grid-cols-2 gap-3">
                <label class="flex flex-col gap-1 text-sm">
                  <span class="font-medium text-gray-700">{{ $gettext("Statut") }}</span>
                  <select v-model="declaration.criminal_investigation.status" class="rounded-md border border-gray-300 px-3 py-2 text-sm">
                    <option :value="null">—</option>
                    <option value="yes">{{ $gettext("Oui") }}</option>
                    <option value="no">{{ $gettext("Non") }}</option>
                    <option value="unknown">{{ $gettext("Inconnu") }}</option>
                  </select>
                </label>
                <CtifFieldRow v-model="declaration.criminal_investigation.file_number" :label="$gettext('Numéro de dossier')" />
                <CtifFieldRow v-model="declaration.criminal_investigation.service" :label="$gettext('Service')" />
                <CtifFieldRow v-model="declaration.criminal_investigation.contact_person" :label="$gettext('Personne de contact')" />
              </div>
              <div class="mt-3">
                <CtifFieldRow v-model="declaration.criminal_investigation.comment" :label="$gettext('Commentaire')" multiline />
              </div>
            </section>

            <!-- Comments -->
            <section>
              <h3 class="mb-3 text-sm font-semibold uppercase text-gray-700">{{ $gettext("Commentaires éventuels") }}</h3>
              <CtifFieldRow v-model="declaration.comments" :label="$gettext('Commentaires')" multiline />
            </section>

            <!-- Signature -->
            <section>
              <h3 class="mb-3 text-sm font-semibold uppercase text-gray-700">{{ $gettext("Signature") }}</h3>
              <div class="grid grid-cols-3 gap-3">
                <CtifFieldRow v-model="declaration.signature.date" :label="$gettext('Date')" type="date" required />
                <CtifFieldRow v-model="declaration.signature.signatory_name" :label="$gettext('Nom')" required />
                <CtifFieldRow v-model="declaration.signature.signatory_role" :label="$gettext('Fonction')" />
              </div>
            </section>

            <footer class="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                class="rounded-md border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200"
                @click="$emit('close')"
              >
                {{ $gettext("Annuler") }}
              </button>
              <button
                type="button"
                class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
                :disabled="downloading"
                @click="onSubmit"
              >
                {{ downloading ? $gettext("Génération…") : $gettext("Télécharger (PDF + XML)") }}
              </button>
            </footer>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  </TransitionRoot>

  <Teleport to="body">
  <div
    v-if="showCtifDialog"
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
  >
    <div class="w-full max-w-md rounded-lg bg-white shadow-xl">
      <div class="border-b border-gray-200 px-6 py-4">
        <h3 class="text-lg font-semibold text-gray-900">{{ $gettext("Déclaration générée") }}</h3>
      </div>
      <div class="px-6 py-4 text-sm text-gray-700">
        {{ $gettext("Téléchargement effectué. Connectez-vous au site CTIF pour la transmettre.") }}
      </div>
      <div class="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
        <button
          type="button"
          class="rounded-md border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200"
          @click="closeAll"
        >
          {{ $gettext("Fermer") }}
        </button>
        <a
          href="https://p-oris.ctif-cfi.be/Home"
          target="_blank"
          rel="noopener noreferrer"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          @click="closeAll"
        >
          {{ $gettext("Ouvrir le site CTIF") }}
        </a>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import CtifFieldRow from "@/components/ctif/CtifFieldRow.vue";
import CtifNaturalPersonFieldset from "@/components/ctif/CtifNaturalPersonFieldset.vue";
import CtifLegalEntityFieldset from "@/components/ctif/CtifLegalEntityFieldset.vue";
import { useCtifDeclarationStore } from "@/stores/ctifDeclaration.js";

const props = defineProps({
  resourceType: { type: String, required: true }, // "companies" | "physical-persons"
  resourceId: { type: String, required: true },
});
const emit = defineEmits(["close"]);

const store = useCtifDeclarationStore(props.resourceType, props.resourceId);
const declaration = ref(null);
const loading = ref(true);
const downloading = ref(false);
const showCtifDialog = ref(false);
const showValidationBanner = ref(false);
const validationBanner = ref(null);

const emptyNaturalPerson = () => ({
  last_name: "", first_name: "", birth_date: "", birth_city: "",
  birth_country: "", national_register_number: "", occupation: "",
  nationality: "", address: { street: "", number: "", zipcode: "", city: "", country: "" },
});

const addMandatary = () => declaration.value.mandataries.push(emptyNaturalPerson());
const addUbo = () => declaration.value.ultimate_beneficial_owners.push(emptyNaturalPerson());

const missingRequired = computed(() => {
  if (!declaration.value) return [];
  const d = declaration.value;
  const missing = [];
  const need = (label, val) => { if (!val || String(val).trim() === "") missing.push(label); };

  need("Déclarant – Nom", d.declarant.last_name);
  need("Déclarant – Prénom", d.declarant.first_name);
  need("Déclarant – Institution", d.declarant.institution_name);

  if (d.client.kind === "legal_entity") {
    const c = d.client.legal_entity;
    need("Client – Dénomination", c.name);
    need("Client – Forme légale", c.legal_form);
    need("Client – Numéro d'entreprise", c.company_number);
  } else {
    const c = d.client.natural_person;
    need("Client – Nom", c.last_name);
    need("Client – Prénom", c.first_name);
    need("Client – Date de naissance", c.birth_date);
    need("Client – Lieu de naissance", c.birth_city);
    need("Client – Pays de naissance", c.birth_country);
    need("Client – Nationalité", c.nationality);
  }

  need("Opération – Devise", d.operation.currency);
  need("Opération – Description", d.operation.description);
  need("Indices BC/FT", d.suspicion_indicators);
  need("Signature – Date", d.signature.date);
  need("Signature – Nom", d.signature.signatory_name);

  return missing;
});

const onSubmit = async () => {
  if (missingRequired.value.length > 0) {
    showValidationBanner.value = true;
    await nextTick();
    validationBanner.value?.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  showValidationBanner.value = false;
  downloading.value = true;
  try {
    await store.downloadPdf(declaration.value);
    await store.downloadXml(declaration.value);
    showCtifDialog.value = true;
  } finally {
    downloading.value = false;
  }
};

const closeAll = () => {
  showCtifDialog.value = false;
  emit("close");
};

onMounted(async () => {
  try {
    declaration.value = await store.fetchPrefill();
    if (!declaration.value.mandataries) declaration.value.mandataries = [];
    if (!declaration.value.ultimate_beneficial_owners) declaration.value.ultimate_beneficial_owners = [];
  } finally {
    loading.value = false;
  }
});
</script>
